<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="element-island flex items-center space-x-4">
          <RouterLink
            :to="{
              name: 'SubcontractorVariationOrdersDashboard',
              params: { projectId: $route.params.projectId }
            }"
            class="btn--secondary-blue btn flex items-center space-x-2"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 mr-2">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
</svg>

            <span>
              All Packages
            </span>
          </RouterLink>

          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ packageData?.name }} Variations Schedule - {{ projectSupplier?.supplier?.name }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              View and manage variation orders for this package
            </p>
          </div>
        </div>

        <div
          class="element-island flex items-center space-x-2"
        >
          <RouterLink
            :to="{
              name: 'PackagesDashboard',
              params: { projectId: $route.params.projectId, packageId: $route.params.packageId }
            }"
            class="btn btn--secondary-blue space-x-2 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>

            <span>View Package</span>
          </RouterLink>

          <button
            v-if="project?.unarchived"
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showManageCategoriesModal = true"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
    />
</svg>

            <span>Categories</span>
          </button>

          <button
            v-if="project?.unarchived"
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showCreateVariationModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>{{ $t('HeadContract.VariationsSchedule.button') }}</span>
          </button>
        </div>
      </div>

      <div class="element-island overflow-y-auto flex flex-col flex-1 p-4">
        <template v-if="variationOrders.length && project">
          <div
            v-show="!variationsExpanded"
            class="flex space-x-4 mb-4"
          >
            <PackageProjectSummary
              :package-budget="packageData?.totalAwarded"
              :variations-summary="variationsSummary"
              :total-variations-budget="totalVariationsBudget"
            />

            <PackageCostSynopsis
              :variations-summary="variationsSummary"
              :project="project"
            />

            <div
              class="flex flex-col space-y-4 w-1/3"
            >
              <div class="flex h-full">
                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    Variations by Status
                  </p>

                  <PackageVariationsStatusGraph
                    class="mt-6"
                    :variation-orders-by-status="variationOrdersByStatus"
                    :variations-summary="variationsSummary"
                  />
                </div>

                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    Variation Status by Value
                  </p>

                  <PackageVariationsValueGraph
                    class="mt-6"
                    :variation-orders-by-status="variationOrdersByStatus"
                    :variations-summary="variationsSummary"
                    :total-variations-budget="totalVariationsBudget"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="variationOrders.length"
            ref="variationsContainer"
            class="w-full overflow-y-auto relative"
          >
            <div class="flex flex-col w-full border border-gray-200 rounded-lg shadow-sm">
              <div
                class="px-2 py-4 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0"
                :class="{ 'z-30': increaseZIndex }"
              >
                <div class="space-y-4">
                  <!-- Filter Header -->
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <button
                        type="button"
                        class="group p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        @click="toggleExpand"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="variationsExpanded"
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                      </button>

                      <h3 class="text-lg font-semibold text-gray-900">
                        Variation Orders
                      </h3>

                      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                        <!-- Search Filter -->
                        <div class="space-y-2">
                          <div class="relative">
                            <AppInputIcon
                              v-model="searchForm.form.search"
                              type="text"
                              name="variation_orders_filter"
                              class="form-input w-full"
                              placeholder="Search variations..."
                            >
                              <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                              </template>
                            </AppInputIcon>
                          </div>
                        </div>

                        <!-- Status Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="searchForm.form.status"
                            name="status_filter"
                            class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="statusOptions"
                            empty-text="All Statuses"
                          />
                        </div>

                        <!-- Sort Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="sortColumn"
                            name="sort_filter"
                            class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="sortOptions"
                            empty-text="Select Sort Option"
                          />
                        </div>

                        <!-- Actions -->
                        <div class="space-y-2">
                          <button
                            type="button"
                            class="btn btn--secondary-blue w-full text-sm"
                            @click="resetSearch"
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>

                    <Transition
                      mode="out-in"
                      enter-active-class="animate__animated animate__fadeIn animate__faster"
                      leave-active-class="animate__animated animate__fadeOut animate__faster"
                    >
                      <p
                        v-if="filteredVariations.length > 0"
                        key="total-variations"
                        class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                      >
                        <span class="font-medium text-gray-900">{{ filteredVariations.length }}&nbsp;</span>

                        <span class="text-gray-500">variation{{ filteredVariations.length !== 1 ? 's' : '' }}</span>
                      </p>
                    </Transition>
                  </div>

                  <!-- Active Filters Display -->
                  <div
                    v-if="hasActiveFilters"
                    class="flex items-center space-x-2 pt-2"
                  >
                    <span class="text-sm text-gray-500">Active Filters:</span>

                    <div class="flex flex-wrap gap-2">
                      <span
                        v-if="searchForm.form.search"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        Search: "{{ searchForm.form.search }}"
                        <button
                          type="button"
                          class="ml-1.5 text-blue-600 hover:text-blue-800"
                          @click="searchForm.form.search = ''"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>

                      <span
                        v-if="!falsey(searchForm.form.status)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, searchForm.form.status)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, searchForm.form.status)] }"
                      >
                        {{ VariationOrderStatusTitles[enumKeyFromValue(VariationOrderStatus, searchForm.form.status)] }}
                        <button
                          type="button"
                          class="ml-1.5"
                          @click="searchForm.form.status = undefined"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <table
                class="border-separate border-spacing-0 w-full z-10 text-sm"
              >
                <thead>
                  <tr class="child:px-2 child:py-3 z-10 sticky top-0 child:border-y child:border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                    <th class="text-left w-60">
                      <div class="flex items-center space-x-2">
                        <button
                          v-if="y > 60"
                          type="button"
                          @click="toggleExpand"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="variationsExpanded"
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                        </button>

                        <span>
                          {{ $t('HeadContract.VariationsSchedule.variation') }}
                        </span>
                      </div>
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.VariationsSchedule.dateOfOccurrence') }}
                    </th>

                    <th class="text-left">
                      {{ project?.isContractor ? $t('HeadContract.VariationsSchedule.clientRef') : $t('HeadContract.VariationsSchedule.contractorRef') }}
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.VariationsSchedule.status') }}
                    </th>

                    <th class="text-right ">
                      {{ $t('HeadContract.VariationsSchedule.amount') }}
                    </th>

                    <th
                      v-if="project?.unarchived"
                      class="text-left w-60"
                    >
                      {{ $t('HeadContract.VariationsSchedule.nextAction') }}
                    </th>

                    <th
                      v-if="project?.unarchived"
                      class="text-left"
                    >
                      {{ $t('HeadContract.VariationsSchedule.assignee') }}
                    </th>

                    <th
                      v-if="project?.unarchived"
                      class="text-left min-w-28"
                    />
                  </tr>
                </thead>

                <tbody>
                  <template v-if="filteredVariations.length > 0">
                    <template
                      v-for="row in groupedScheduleRows"
                      :key="row.type === 'category' ? `category-${row.id}` : row.variation.id"
                    >
                      <VariationCategoryHeaderRow
                        v-if="row.type === 'category'"
                        :name="row.name"
                        :colspan="variationTableColspan"
                      />

                      <PackageVariationOrderRow
                        v-else
                        :variation-order="row.variation"
                        @show-variation="showVariation(row.variation)"
                        @show-variation-attachments="showVariationAttachments(row.variation)"
                        @open-comment-modal="openCommentModal(row.variation)"
                        @accept-variation-order="acceptVariationOrder(row.variation)"
                        @decline-variation-order="declineVariationOrder(row.variation)"
                        @price-variation-order="selectedVariation = row.variation.id; showModal = true;"
                        @submit-variation-order="submitVariationOrder(row.variation.id)"
                        @approve-variation-order="approveVariationOrder(row.variation.id)"
                        @sum-set-variation-order="priceVariationOrder($event.variationOrder, $event.sumPrice)"
                        @save-next-action="updateNextActionVariationOrder($event.variationOrder, $event.next_action, $event.next_action_due_date, $event.next_action_assignee_id)"
                        @send-notice="sendNoticeForVariationOrder($event)"
                        @register-notice="registerNotice($event)"
                        @register-pricing-notice="registerPricingNotice($event)"
                        @open-correspondence-modal="openCorrespondenceModal($event)"
                        @principle-variation-order="principleVariationOrder($event)"
                      />
                    </template>

                    <tr
                      v-if="!searchForm.form.search"
                      class="sticky bottom-0 bg-white border child:px-2 child:border-y first-child:border-l last-child:border-r font-semibold"
                    >
                      <td />

                      <td />

                      <td />

                      <td>
                        Total
                      </td>

                      <td class="text-right">
                        {{ australianCurrency(totalVariationsBudget) }}
                      </td>

                      <td v-if="project?.unarchived" />

                      <td v-if="project?.unarchived" />

                      <td v-if="project?.unarchived" />
                    </tr>
                  </template>

                  <tr v-else>
                    <td
                      :colspan="variationTableColspan"
                      class="px-6 py-16 text-center"
                    >
                      <div class="flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                        <h3 class="text-sm font-medium text-gray-900 mb-1">
                          No variations found
                        </h3>

                        <p class="text-sm text-gray-500">
                          Adjust your filters to see more results
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <div
          v-else-if="indexPackageVariationsActions.is(States.COMPLETE)"
          class="flex flex-col items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            No variation orders found
          </h3>

          <p class="text-sm text-gray-500">
            This package doesn't have any variation orders yet.
          </p>
        </div>

        <div v-else>
          <div class="flex flex-col items-center">
            <AppSpinner />
          </div>

          <p class="text-sm text-gray-500">
            Loading variation orders...
          </p>
        </div>
      </div>
    </div>

    <RegisterNoticeModal
      v-if="showRegisterModal"
      :variation-order-id="selectedVariation"
      @close="showRegisterModal = false"
    />

    <RegisterPricingModal
      v-if="showPricingRegisterModal"
      :variation-order-id="selectedVariation"
      @close="showPricingRegisterModal = false"
    />

    <PricingModal
      v-if="showModal"
      :variation-order-id="selectedVariation"
      @close="showModal = false"
    />

    <PackageVariationSaveModal
      v-if="showCreateVariationModal"
      :project-id="props.projectId"
      :package-id="props.packageId"
      @close="showCreateVariationModal = false"
    />

    <PackageVariationOrderCategoriesModal
      v-if="showManageCategoriesModal"
      :project-id="props.projectId"
      :package-id="props.packageId"
      @close="showManageCategoriesModal = false"
    />

    <PackageVariationShowModal
      v-if="showVariationModal"
      :variation-order-id="selectedVariation"
      :start-on-attachments="startOnAttachments"
      @close="closeShowModal"
      @back-charged="onBackCharged"
    />

    <PackageVariationCommentModal
      v-if="showCommentModal"
      :project-id="props.projectId"
      :variation-id="selectedVariation"
      @close="showCommentModal = false"
    />

    <VariationCorrespondenceModal
      v-if="showVariationCorrespondenceModal"
      :variation-order-id="selectedVariation"
      @close="showVariationCorrespondenceModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import VariationCategoryHeaderRow from '@/components/HeadContract/VariationsSchedule/VariationCategoryHeaderRow.vue';
import PackageCostSynopsis from '@/components/Subcontract/PackageVariationsSchedule/PackageCostSynopsis.vue';
import PackageProjectSummary from '@/components/Subcontract/PackageVariationsSchedule/PackageProjectSummary.vue';
import PackageVariationCommentModal from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationCommentModal.vue';
import PackageVariationOrderCategoriesModal from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationOrderCategoriesModal.vue';
import PackageVariationOrderRow from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationOrderRow.vue';
import PackageVariationSaveModal from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationSaveModal.vue';
import PackageVariationShowModal from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationShowModal.vue';
import PackageVariationsStatusGraph from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationsStatusGraph.vue';
import PackageVariationsValueGraph from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationsValueGraph.vue';
import PricingModal from '@/components/Subcontract/PackageVariationsSchedule/PricingModal.vue';
import RegisterNoticeModal from '@/components/Subcontract/PackageVariationsSchedule/RegisterNoticeModal.vue';
import RegisterPricingModal from '@/components/Subcontract/PackageVariationsSchedule/RegisterPricingModal.vue';
import VariationCorrespondenceModal from '@/components/Subcontract/PackageVariationsSchedule/VariationCorrespondenceModal.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, enumKeyFromValue, falsey, timeZone } from '@/helpers';
import { t } from '@/i18n';
import Package from '@/models/Package';
import VariationOrder, {
  VariationOrderStatus,
  VariationOrderStatusColours,
  VariationOrderStatusTextColours,
  VariationOrderStatusTitles,
  VariationOrderType,
} from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { useDebounceFn, useScroll } from '@vueuse/core';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { useCollect } from 'pinia-orm/dist/helpers';
import Swal from 'sweetalert2';
import { useTemplateRef } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  packageId: string;
  variationId?: string;
}>();

const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCategoriesStore = useVariationOrderCategoriesStore();
const { project } = storeToRefs(projectsStore);
const router = useRouter();
const showPackageAction = useStoreApiAction(packagesStore.show);
const indexPackageVariationsActions = useStoreApiAction(variationOrdersStore.indexPackageVariationOrders);
const fetchVariationOrderCategoriesAction = useStoreApiAction(variationOrderCategoriesStore.indexPackage);

watch(() => props.projectId, (newValue, oldValue) => {
  if(oldValue && newValue !== oldValue) {
    router.push({ name: 'SubcontractorVariationOrdersDashboard' });
  }
});

const showVariationModal = ref(false);
const selectedVariation = ref();
const startOnAttachments = ref(false);

watch(() => props.packageId, () => {
  showPackageAction.request(props.packageId);
  indexPackageVariationsActions.request(props.projectId, props.packageId);
  fetchVariationOrderCategoriesAction.request(props.projectId, props.packageId).catch(console.error);

  if(props.variationId) {
    selectedVariation.value = parseInt(props.variationId);
    startOnAttachments.value = false;
    showVariationModal.value = true;
  }
}, { immediate: true });

const packageData = computed(() => {
  return packagesStore.models
    .where('id', parseInt(props.packageId))
    .with('subcontractor', (query) => {
      query.with('projects', (query) => {
        query.where('project_id', parseInt(props.projectId))
          .with('supplier');
      });
    })
    .first();
});

const projectSupplier = computed(() => {
  return packageData.value?.subcontractor?.projects?.[0];
});

const variationOrders = computed(() => {
  return variationOrdersStore.models
    .where('package_id', parseInt(props.packageId))
    .with('trade_items')
    .with('next_action_assignee')
    .with('variation_order_category')
    .get();
});

// const packageBudget = computed(() => {
//   return packageData.value?.budget || 0;
// });

// const totalVariationValue = computed(() => {
//   return variationOrders.value.reduce((total, vo) => total + (vo.budget || 0), 0);
// });

const totalVariationsBudget = computed(() => {
  return variationsSummary.value.unapproved_variations_total + variationsSummary.value.approved_variations_total +
    variationsSummary.value.completed_variations_total;
});

const variationsSummary = ref<VariationOrdersSummary>({
  approved_variations_total: 0,
  unapproved_variations_total: 0,
  completed_variations_total: 0,
  priced: 0,
  submitted: 0,
  provisioned: 0,
  approved: 0,
  principled: 0,
  completed: 0,
  total_variations: 0,
  approved_count: 0,
  unapproved_count: 0,
  total_budget: 0,
});

const variationOrdersByStatus = computed(() => {
  return variationOrders.value.reduce((variationsMap, variation) => {
    const key = enumKeyFromValue(VariationOrderStatus, variation.status);

    if(key === 'declined') {
      return variationsMap;
    }

    if(variationsMap.has(key)) {
      const variations = variationsMap.get(key);

      variations.push(variation);
      variationsMap.set(key, variations);
    } else {
      variationsMap.set(key, [variation]);
    }

    return variationsMap;
  }, new Map<string, VariationOrder[]>());
});

const calculateVariationsSummary = () => {
  const summary = {
    approved_variations_total: 0,
    unapproved_variations_total: 0,
    completed_variations_total: 0,
    priced: 0,
    submitted: 0,
    provisioned: 0,
    approved: 0,
    principled: 0,
    completed: 0,
    total_variations: variationOrders.value.length,
    approved_count: 0,
    unapproved_count: 0,
    total_budget: 0,
  };

  variationOrders.value.forEach((vo) => {
    const budget = vo.totalBudget || 0;

    summary.total_budget += budget;

    switch (vo.status) {
      case VariationOrderStatus.priced:
        summary.priced += budget;
        summary.unapproved_count++;
        summary.unapproved_variations_total += budget;
        break;

      case VariationOrderStatus.submitted:
        summary.submitted += budget;
        summary.unapproved_count++;
        summary.unapproved_variations_total += budget;
        break;

      case VariationOrderStatus.principled:
        summary.principled += budget;
        summary.unapproved_count++;
        summary.unapproved_variations_total += budget;
        break;

      case VariationOrderStatus.provisioned:
        summary.provisioned += budget;
        summary.unapproved_count++;
        summary.unapproved_variations_total += budget;
        break;

      case VariationOrderStatus.approved:
        summary.approved += budget;
        summary.approved_count++;
        summary.approved_variations_total += budget;
        break;

      case VariationOrderStatus.completed:
        summary.completed_variations_total += budget;
        summary.completed++;
        break;
    }
  });

  variationsSummary.value = summary;
};

watch(variationOrders, () => {
  calculateVariationsSummary();
}, { immediate: true });

const sortColumn = ref<
  'status' | 'category' | 'code' | 'date_of_occurrence' | 'created_at' | 'amount' | 'next_action' | 'back_charges'
>(
  'category',
);

const sortOptions = computed(() => {
  return [
    {
      value: 'category',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.category'),
    },
    {
      value: 'status',
      label: 'Status',
    },
    {
      value: 'date_of_occurrence',
      label: 'Date of Occurrence',
    },
    {
      value: 'created_at',
      label: 'Latest',
    },
    {
      value: 'amount',
      label: 'Amount',
    },
    {
      value: 'next_action',
      label: 'Next Action Due Date',
    },
    {
      value: 'back_charges',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.backCharges'),
    },
  ];
});

const sortedVariationOrders = computed(() => {
  let variations = [];

  switch (sortColumn.value) {
    case 'category':

      // fall-through
    case 'status':
      variations = useCollect(variationOrders.value).sortBy([['status', 'asc'], ['created_at', 'desc']]);
      break;

    case 'date_of_occurrence':

      // Fall through
    case 'created_at':
      variations = useCollect(variationOrders.value).sortBy([[sortColumn.value, 'desc']]);
      break;

    case 'amount':
      variations = useCollect(variationOrders.value).sortBy((model) => -(model.budget || 0));
      break;

    case 'code':
      variations = variationOrders.value.toSorted((a, b) => {
        if(!falsey(a.code) && !falsey(b.code)) {
          return a.code.localeCompare(b.code);
        } else if(falsey(a.code) && !falsey(b.code)) {
          return 1;
        }

        return -1;
      });

      break;

    case 'next_action':
      variations = variationOrders.value.toSorted((a, b) => {
        if(!falsey(a.next_action_due_date) && !falsey(b.next_action_due_date)) {
          return new Date(a.next_action_due_date).getTime() - new Date(b.next_action_due_date).getTime();
        } else if(falsey(a.next_action_due_date) && !falsey(b.next_action_due_date)) {
          return 1;
        }

        return -1;
      });

      break;

    case 'back_charges':
      variations = useCollect(variationOrders.value).sortBy([['created_at', 'desc']]);
      break;
  }

  return variations;
});

const searchForm = useFormData({
  search: '',
  status: undefined,
});

const statusOptions = computed(() => {
  const options = [];

  for(const key in VariationOrderStatus) {
    options.push({ value: VariationOrderStatus[key], label: VariationOrderStatusTitles[key] });
  }

  return options;
});

const filteredVariations = computed(() => {
  let filtered = sortedVariationOrders.value;

  if(searchForm.form.search) {
    const searchTerm = searchForm.form.search.toLowerCase();

    filtered = filtered.filter((variation) => {
      return (
        variation.code?.toLowerCase().includes(searchTerm) ||
        variation.description?.toLowerCase().includes(searchTerm)
      );
    });
  }

  if(searchForm.form.status !== undefined) {
    filtered = filtered.filter((variation) => variation.status === searchForm.form.status);
  }

  if(sortColumn.value === 'back_charges') {
    filtered = filtered.filter((variation) => {
      return variation.type === VariationOrderType.other && !falsey(variation.transferred_from_variation_order_id);
    });
  }

  return filtered;
});

const variationOrderCategories = computed(() => {
  return variationOrderCategoriesStore.models
    .where('project_id', parseInt(props.projectId))
    .where('package_id', parseInt(props.packageId))
    .get();
});

const variationTableColspan = computed(() => {
  if(!project.value?.unarchived) {
    return 5;
  }

  return 8;
});

const groupedScheduleRows = computed(() => {
  if(sortColumn.value !== 'category') {
    return filteredVariations.value.map((variation) => ({
      type: 'variation' as const,
      variation,
    }));
  }

  const uncategorized: VariationOrder[] = [];
  const grouped = new Map<number, { name: string; sortOrder: number; variations: VariationOrder[] }>();

  const categoriesById = new Map(
    variationOrderCategories.value.map((category) => [category.id, category]),
  );

  for(const variation of filteredVariations.value) {
    const categoryId = variation.variation_order_category_id;

    if(falsey(categoryId)) {
      uncategorized.push(variation);
      continue;
    }

    const existing = grouped.get(categoryId);

    if(existing) {
      existing.variations.push(variation);
      continue;
    }

    const category = categoriesById.get(categoryId) ?? variation.variation_order_category;

    grouped.set(categoryId, {
      name: category?.description ?? '',
      sortOrder: category?.sort_order ?? 0,
      variations: [variation],
    });
  }

  const rows: ({ type: 'category'; id: number; name: string } | { type: 'variation'; variation: VariationOrder })[] =
    uncategorized.map((variation) => ({
      type: 'variation',
      variation,
    }));

  [...grouped.entries()]
    .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
    .forEach(([id, group]) => {
      rows.push({
        type: 'category',
        id,
        name: group.name,
      });

      group.variations.forEach((variation) => {
        rows.push({
          type: 'variation',
          variation,
        });
      });
    });

  return rows;
});

const resetSearch = () => {
  searchForm.resetData();
};

const variationsExpanded = ref(false);

const toggleExpand = () => {
  variationsExpanded.value = !variationsExpanded.value;
};

const variationsContainer = useTemplateRef('variationsContainer');
const { y } = useScroll(variationsContainer);
const increaseZIndex = ref(true);

watch(y, (newValue) => {
  if(newValue > 0) {
    increaseZIndex.value = false;
  } else {
    increaseZIndex.value = true;
  }
});

const hasActiveFilters = computed(() => {
  return searchForm.isDirty.value || variationsExpanded.value;
});

const showCreateVariationModal = ref(false);
const showManageCategoriesModal = ref(false);
const showCommentModal = ref(false);
const showRegisterModal = ref(false);
const showPricingRegisterModal = ref(false);
const showVariationCorrespondenceModal = ref(false);
const showModal = ref(false);
const acceptVariationOrderAction = useStoreApiAction(variationOrdersStore.acceptVariationOrder);
const declineVariationOrderAction = useStoreApiAction(variationOrdersStore.declineVariationOrder);
const submitVariationOrderAction = useStoreApiAction(variationOrdersStore.submitVariationOrder);
const approveVariationOrderAction = useStoreApiAction(variationOrdersStore.approveVariationOrder);
const principleVariationOrderAction = useStoreApiAction(variationOrdersStore.principleVariationOrder);
const priceVariationOrderAction = useStoreApiAction(variationOrdersStore.priceVariationOrder);
const sendNoticeForVariationOrderAction = useStoreApiAction(variationOrdersStore.sendNoticeForVariationOrder);
const updateNextActionVariationOrderAction = useStoreApiAction(variationOrdersStore.updateNextActionVariationOrder);

const updateNextActionVariationOrder = (
  variationOrder: VariationOrder,
  next_action: string,
  next_action_due_date: string,
  next_action_assignee_id: number,
) => {
  updateNextActionVariationOrderAction.request(variationOrder.id, {
    next_action,
    next_action_due_date,
    next_action_assignee_id,
  }).catch(console.error);
};

// Action handler functions
const acceptVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Accept Variation Order?',
    icon: 'question',
    text: variationOrder.description,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      acceptVariationOrderAction.request(variationOrder.id).catch((error) => {
        console.log(error);
      });
    }
  });
};

const declineVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Decline Variation Order?',
    icon: 'question',
    text: variationOrder.description,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      declineVariationOrderAction.request(variationOrder.id).then(() => {
        console.log('declined');
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const submitVariationOrder = (id: number) => {
  Swal.fire({
    title: 'Submit Variation Order?',
    text: 'Submit variation order pricing to be approved by client',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.variation_submission_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      submitVariationOrderAction.request(id, { description: result.value, time_zone: timeZone }).then((data) => {
        Swal.fire('Variation Order Submitted', '', 'success');
      }).catch((error) => {
        Swal.fire('Failed to Submit', error?.data?.message ?? 'Failed to submit variation order', 'error');
      });
    }
  });
};

const approveVariationOrder = (id: number) => {
  Swal.fire({
    title: 'Approve Variation Order?',
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      approveVariationOrderAction.request(id, { time_zone: timeZone }).then((data) => {
        console.log(data);
      }).catch((error) => {
        Swal.fire('Failed to Approve', '', 'error');
      });
    }
  });
};

const principleVariationOrder = (variation: VariationOrder) => {
  Swal.fire({
    title: 'Approve in Principle?',
    text: `Would you like to set variation ${variation.code} to approved in principle?`,
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      principleVariationOrderAction.request(variation.id).catch((error) => {
        Swal.fire('Failed to Approve in Principle', '', 'error');
      });
    }
  });
};

const priceVariationOrder = (variationOrder: VariationOrder, budget: number) => {
  priceVariationOrderAction.request(variationOrder.id, { budget }).then(() => {
    calculateVariationsSummary();
  }).catch((error) => {
    console.log(error);
  });
};

const sendNoticeForVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Send Notice for Variation Order?',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.nov_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      sendNoticeForVariationOrderAction.request(variationOrder.id, { description: result.value, time_zone: timeZone })
        .then((data) => {
          Swal.fire('Notice Sent', '', 'success');
        }).catch((error) => {
          Swal.fire('Failed to Send Notice', error?.data?.message, 'error');
        });
    }
  });
};

// Modal handlers
const showVariation = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  startOnAttachments.value = false;
  showVariationModal.value = true;

  router.replace({ params: { variationId: variation.id.toString() } });
};

const showVariationAttachments = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  startOnAttachments.value = true;
  showVariationModal.value = true;

  router.replace({ params: { variationId: variation.id.toString() } });
};

const closeShowModal = () => {
  showVariationModal.value = false;
  startOnAttachments.value = false;

  router.replace({ params: { variationId: '' } });
};

const onBackCharged = () => {
  indexPackageVariationsActions.request(props.projectId, props.packageId);
};

const openCommentModal = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  showCommentModal.value = true;
};

const registerNotice = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  showRegisterModal.value = true;
};

const registerPricingNotice = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  showPricingRegisterModal.value = true;
};

const openCorrespondenceModal = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  showVariationCorrespondenceModal.value = true;
};

useBoqLockRedirect();
</script>

<style scoped></style>
